import React, { useEffect, useState } from "react";
import {
  Form,
  Popconfirm,
  message,
  Dropdown,
  Input,
  Button,
  Space,
} from "antd";
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  TagOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { BsThreeDotsVertical } from "react-icons/bs";
import GenericTable from "../../../Components/Tables/GenericTable";
import GenericModalForm from "../../../Components/Modals/GenericModalForm";
import {
  getFuelPricing,
  getFuelPricingById,
  createFuelPricing,
  updateFuelPricing,
  deleteFuelPricing,
  getFuelTypesOffered,
} from "../../../API/FuelVendor/FuelPricing";
import "./style.css";
import usePageAccess from "../../../Utlis/usePageAccess";

const getErrorMessage = (data, fallback) => {
  if (!data) return fallback;
  if (data.message) return data.message;
  if (data.detail) return data.detail;

  const fieldErrors = Object.values(data)
    .flat()
    .filter((msg) => typeof msg === "string");

  return fieldErrors.length ? fieldErrors.join(" ") : fallback;
};

const mapFuelPrice = (item, index) => {
  const currentPrice = parseFloat(
    item.price ?? item.current_price ?? item.price_per_liter,
  );
  const previous =
    item.previous_price !== undefined && item.previous_price !== null
      ? parseFloat(item.previous_price)
      : null;

  return {
    key: String(item.id ?? index),
    fuelTypeId: item.fuel_type,
    fuelType:
      item.fuel_type_name || item.fuel_type || item.name || item.code || "-",
    currentPrice: Number.isNaN(currentPrice) ? 0 : currentPrice,
    previous: Number.isNaN(previous) ? null : previous,
    deltaPercent:
      item.price_change_percent !== undefined &&
      item.price_change_percent !== null
        ? Math.abs(parseFloat(item.price_change_percent))
        : null,
    deltaUp: item.price_change_direction
      ? item.price_change_direction === "increase"
      : null,
  };
};

const FuelPricing = () => {
  const { canEdit } = usePageAccess("fuel-pricing");
  const [data, setData] = useState([]);
  const [fuelTypeOptions, setFuelTypeOptions] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  const [editRecord, setEditRecord] = useState(null);
  const [deleteRecord, setDeleteRecord] = useState(null);
  const [isAddFuelOpen, setIsAddFuelOpen] = useState(false);

  useEffect(() => {
    const fetchFuelPricing = async () => {
      try {
        const res = await getFuelPricing();
        const list = Array.isArray(res?.data)
          ? res.data
          : res?.data?.data || res?.data?.results || [];

        if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
          setData(list.map(mapFuelPrice));
        } else {
          message.error(
            getErrorMessage(res?.data, "Failed to load fuel pricing."),
          );
        }
      } catch (err) {
        message.error(
          getErrorMessage(err?.response?.data, "Failed to load fuel pricing."),
        );
      }
    };

    fetchFuelPricing();
  }, []);

  const fetchFuelTypesOffered = async () => {
    try {
      const res = await getFuelTypesOffered();
      const list = Array.isArray(res?.data)
        ? res.data
        : res?.data?.data || res?.data?.results || [];

      if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
        setFuelTypeOptions(list.map((f) => ({ label: f.name, value: f.id })));
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to load fuel types offered."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(
          err?.response?.data,
          "Failed to load fuel types offered.",
        ),
      );
    }
  };

  useEffect(() => {
    if (isAddFuelOpen) fetchFuelTypesOffered();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAddFuelOpen]);

  useEffect(() => {
    fetchFuelTypesOffered();
  }, []);

  /* ── Add ── */
  const handleAddFuel = async (values) => {
    try {
      const res = await createFuelPricing({
        fuel_type: Number(values.fuelType),
        price: values.currentPrice,
      });

      if (Math.floor(res?.status / 100) === 2) {
        const item = res?.data?.data;
        if (item) {
          setData((prev) => [mapFuelPrice(item), ...prev]);
        }
        setIsAddFuelOpen(false);
        message.success(
          getErrorMessage(res?.data, "Fuel type added successfully"),
        );
      } else {
        message.error(getErrorMessage(res?.data, "Failed to add fuel type."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to add fuel type."),
      );
    }
  };

  /* ── Delete ── */
  const handleDelete = async (key) => {
    try {
      const res = await deleteFuelPricing(key);

      if (Math.floor(res?.status / 100) === 2) {
        setData((prev) => prev.filter((r) => r.key !== key));
        message.success(
          getErrorMessage(res?.data, "Fuel type deleted successfully"),
        );
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to delete fuel type."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to delete fuel type."),
      );
    }
  };

  /* ── Edit ── */
  const openEdit = async (record) => {
    setEditRecord(record);
    setEditOpen(true);

    try {
      const res = await getFuelPricingById(record.key);
      const item = res?.data?.data;

      if (Math.floor(res?.status / 100) === 2 && item) {
        const mapped = mapFuelPrice(item);
        setEditRecord(mapped);
        setData((prev) =>
          prev.map((r) => (r.key === mapped.key ? mapped : r)),
        );
      }
    } catch (err) {
      console.error("Failed to load latest fuel price:", err);
    }
  };

  const handleEdit = async (values) => {
    try {
      const res = await updateFuelPricing(editRecord.key, {
        price: values.currentPrice,
      });

      if (Math.floor(res?.status / 100) === 2) {
        const item = res?.data?.data;
        const mapped = item
          ? mapFuelPrice(item)
          : {
              ...editRecord,
              previous: editRecord.currentPrice,
              currentPrice: values.currentPrice,
            };

        setData((prev) =>
          prev.map((r) => (r.key === editRecord.key ? mapped : r)),
        );
        setEditOpen(false);
        setEditRecord(null);
        message.success(
          getErrorMessage(res?.data, "Fuel price updated successfully"),
        );
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to update fuel price."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to update fuel price."),
      );
    }
  };

  const getEditInitialValues = () => {
    if (!editRecord) return {};
    return {
      currentPrice: editRecord.currentPrice,
    };
  };

  const getEditFuelModalFields = () => [
    {
      name: "currentPrice",
      label: "Current Price",
      type: "inputNumber",
      rules: [{ required: true, message: "Price is required" }],
    },
  ];

  const getDelta = (record) => {
    if (record.deltaPercent !== null && record.deltaPercent !== undefined) {
      return record.deltaUp ? record.deltaPercent : -record.deltaPercent;
    }
    if (!record.previous) return 0;
    return ((record.currentPrice - record.previous) / record.previous) * 100;
  };

  const fuelTypeFilters = [...new Set(data.map((item) => item.fuelType))].map(
    (type) => ({
      text: type,
      value: type,
    }),
  );

  const columns = [
    {
      title: "Fuel Type",
      dataIndex: "fuelType",
      key: "fuelType",

      filters: fuelTypeFilters,
      onFilter: (value, record) => record.fuelType === value,

      render: (v) => <span className="fv-pricing__fuel-name">{v}</span>,
    },
    {
      title: "Current Price",
      dataIndex: "currentPrice",
      key: "currentPrice",
      sorter: (a, b) => a.currentPrice - b.currentPrice,
      render: (v) => (
        <span className="fv-pricing__price-badge">
          <TagOutlined className="fv-pricing__price-icon" />
          <span>$ {v.toFixed(2)}</span>
        </span>
      ),
    },
    {
      title: "Previous",
      dataIndex: "previous",
      key: "previous",
      sorter: (a, b) => a.previous - b.previous,
      render: (v) => (
        <span className="fv-pricing__previous">
          {v === null || v === undefined ? "-" : `$ ${v.toFixed(2)}`}
        </span>
      ),
    },
    {
      title: "Delta",
      dataIndex: "delta",
      key: "delta",
      sorter: (a, b) => getDelta(a) - getDelta(b),
      render: (_, record) => {
        const v = getDelta(record);
        const up = v >= 0;

        return (
          <span className={`fv-pricing__delta ${up ? "up" : "down"}`}>
            {up ? <ArrowUpOutlined /> : <ArrowDownOutlined />}
            {Math.abs(v).toFixed(1)}%
          </span>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const items = [
          {
            key: "edit",
            label: (
              <div
                onClick={() => openEdit(record)}
                style={{ display: "flex", alignItems: "center", gap: 8 }}
              >
                <EditOutlined /> Edit
              </div>
            ),
          },
          {
            key: "delete",
            label: (
              <div
                onClick={() => setDeleteRecord(record)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  color: "#DC2626",
                }}
              >
                <DeleteOutlined /> Delete
              </div>
            ),
          },
        ];

        return (
          <>
            <Dropdown
              menu={{ items }}
              trigger={["click"]}
              placement="bottomRight"
            >
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  width: 24,
                  height: 24,
                }}
              >
                <BsThreeDotsVertical
                  style={{ fontSize: 20, color: "#4F46E5" }}
                />
              </span>
            </Dropdown>

            <Popconfirm
              open={deleteRecord?.key === record.key}
              title="Delete Fuel Type"
              description={`Are you sure you want to delete "${deleteRecord?.fuelType}"?`}
              onConfirm={() => {
                handleDelete(deleteRecord.key);
                setDeleteRecord(null);
              }}
              onCancel={() => setDeleteRecord(null)}
              okText="Delete"
              cancelText="Cancel"
              okButtonProps={{ danger: true }}
            />
          </>
        );
      },
    },
  ];

  const getFuelModalFields = () => [
    {
      name: "fuelType",
      label: "Select Fuel Type",
      type: "select",
      placeholder: "Select Fuel Type",
      options: fuelTypeOptions,
      onFocus: () => fetchFuelTypesOffered(),
      rules: [{ required: true, message: "Fuel type is required" }],
    },
    {
      name: "currentPrice",
      label: "Current Price ($/Liter)",
      type: "inputNumber",
      placeholder: "0.00",
      rules: [{ required: true, message: "Price is required" }],
    },
  ];

  return (
    <div className="fv-pricing">
      <GenericTable
        title="Fuel Pricing"
        dataSource={data}
        columns={columns.filter((c) => canEdit || c.key !== "actions")}
        enableRowSelection={false}
        headerButtonText={canEdit ? "Add Fuel Type" : undefined}
        headerButtonIcon={<PlusOutlined />}
        onHeaderButtonClick={() => setIsAddFuelOpen(true)}
      />

      {/* Add Modal */}
      <GenericModalForm
        open={isAddFuelOpen}
        title="Add New Fuel Type"
        okText="Add Fuel"
        cancelText="Cancel"
        fields={getFuelModalFields()}
        onCancel={() => setIsAddFuelOpen(false)}
        onOk={handleAddFuel}
      />

      {/* Edit Modal */}
      <GenericModalForm
        key={editRecord?.key || "edit-modal"}
        open={editOpen}
        title={
          <div>
            <div>Edit Fuel Price</div>
            {editRecord && (
              <div
                style={{
                  fontSize: "14px",
                  color: "#6B7280",
                  fontWeight: "normal",
                  marginTop: "4px",
                }}
              >
                Fuel Type: <strong>{editRecord.fuelType}</strong>
              </div>
            )}
          </div>
        }
        okText="Update"
        cancelText="Cancel"
        fields={getEditFuelModalFields()}
        initialValues={getEditInitialValues()}
        onCancel={() => {
          setEditOpen(false);
          setEditRecord(null);
        }}
        onOk={handleEdit}
      />
    </div>
  );
};

export default FuelPricing;
